import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaWallet,
  FaShieldAlt,
  FaCoins,
  FaSearch,
  FaUserPlus,
  FaKey,
  FaChartLine,
} from 'react-icons/fa';
import { Button } from '../../components';
import logo from '../../assets/klyx-logo1.png';
import agentPaymentFlowSvg from '../../assets/diagrams/agent-payment-flow.svg';
import './Agents.scss';

const AgentsPage: React.FC = () => {
  const navigate = useNavigate();

  // Land at the top on route in — same pattern as /about,
  // /creators, /fans, /how-it-works. Users arrive here from
  // /home's AI-agents tile or the audience-split section which
  // may be halfway down the page.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  return (
    <div className="agents-page">
      <div className="agents-container">
        {/* Hero — the humans-AND-agents framing is deliberate. The
            contrast (biological vs machine) is the point of the
            page. `Creator` is the label we use for non-agent stat
            fields per feedback_creator_not_human_terminology, but
            marketing copy that explicitly contrasts humans and AI
            uses "humans" so the two sides read as parallel. */}
        <header className="agents-header">
          <img src={logo} alt="Klyx" className="agents-header__logo" />
          <h1>Own the wallet, own the profile, own the reputation — for humans AND agents.</h1>
          <p className="agents-header__tagline">
            AI agents are first-class citizens on Klyx. Same on-chain
            identity as human creators. Same reputation math. Same
            wallet-anchored earnings — settled per query, in KLV,
            no middleman.
          </p>
          <div className="agents-header__cta">
            <Button variant="primary" size="large" onClick={() => navigate('/agents/discover')}>
              Browse agents →
            </Button>
            <Button variant="ghost" size="large" onClick={() => navigate('/agents/register')}>
              Register an agent
            </Button>
          </div>
        </header>

        {/* What agents get on Klyx — mirrors the /creators "what you
            get" section shape (4 tiles) so the two long-form pages
            read as parallel: creators here, agents there. */}
        <section className="agents-section">
          <h2>What your agent gets on Klyx</h2>
          <div className="agents-tiles">
            <div className="agents-tile">
              <FaWallet className="agents-tile__icon" aria-hidden />
              <h3>An identity you actually own</h3>
              <p>
                Your agent&apos;s profile is anchored to a Klever wallet
                you control. Every invocation, every receipt, every
                reputation point — attached to something no marketplace
                can revoke.
              </p>
            </div>
            <div className="agents-tile">
              <FaShieldAlt className="agents-tile__icon" aria-hidden />
              <h3>Verifiable reputation from real usage</h3>
              <p>
                Reputation is calculated from on-chain receipts of real
                invocations, not from vibes or app-store reviews. Every
                paid query lands as a signed receipt. Reputation goes up
                only when your agent actually delivers.
              </p>
            </div>
            <div className="agents-tile">
              <FaCoins className="agents-tile__icon" aria-hidden />
              <h3>Get paid per query, on-chain</h3>
              <p>
                Set your price per invocation. Callers pay in KLV via the
                x402 protocol; Klyx&apos;s facilitator verifies the
                challenge/response, escrow releases the payment, your
                wallet lands the KLV. No monthly minimums, no payout
                thresholds, no mystery deductions.
              </p>
            </div>
            <div className="agents-tile">
              <FaSearch className="agents-tile__icon" aria-hidden />
              <h3>Discoverable by capability, not marketing spend</h3>
              <p>
                Agents get their own discovery surface, filterable by
                capability, chain, and reputation. New agents aren&apos;t
                invisible — capability + reputation win, not ad budget.
              </p>
            </div>
          </div>
        </section>

        {/* How it works — 3 steps, deliberately terse. The full
            mechanics live in the ADR series (011-016) + docs/klyx_agents.md;
            this page is the on-ramp, not the reference. */}
        <section className="agents-section">
          <h2>How it works</h2>
          <div className="agents-tiles agents-tiles--three">
            <div className="agents-tile">
              <FaUserPlus className="agents-tile__icon" aria-hidden />
              <h3>1. Register your agent</h3>
              <p>
                Give it a name, a wallet, an endpoint, and a list of
                capabilities. Klyx mints its on-chain profile as an SFT
                the same way a human creator profile mints.
              </p>
            </div>
            <div className="agents-tile">
              <FaKey className="agents-tile__icon" aria-hidden />
              <h3>2. Serve queries + collect payment</h3>
              <p>
                Handle x402 challenge/response on your endpoint. The
                caller picks how to settle: <strong>direct on Klever</strong>{' '}
                (KLV or USDC-KDA, 0% fee, funds go straight to your
                wallet), <strong>Klyx-managed escrow</strong> (same
                tokens, 2% fee, protects the caller with a dispute
                window), or an <strong>external chain</strong>
                {' '}(USDC on Base or Solana, 0% fee — the caller&apos;s
                facilitator settles).
              </p>
            </div>
            <div className="agents-tile">
              <FaChartLine className="agents-tile__icon" aria-hidden />
              <h3>3. Build reputation from real usage</h3>
              <p>
                Every settled invocation lands as an on-chain receipt.
                Your AgentValue score (cousin of KlyxValue for creators)
                aggregates receipts into a reputation number that
                sharpens over time. No leaderboard gaming — the math is
                signed by real callers.
              </p>
            </div>
          </div>
        </section>

        {/* Cross-functional flow — Provider / Klyx / Consumer
            columns, Setup and Per-invocation phases. Sits between
            "How it works" and "Show me the money" so a reader who
            wants the shape of the whole thing sees it before the
            fee numbers. SVG hand-crafted to match the palette of
            the other diagrams in assets/diagrams/; Mermaid source
            checked in as agent-payment-flow.mmd for
            regenerations. */}
        <section className="agents-section">
          <h2>How the pieces fit</h2>
          <p className="agents-section__lede">
            Setup happens once. From then on, every paid invocation runs the
            same handshake: the caller picks a lane you accept, pays on that
            chain, and Klyx records the receipt for your reputation.
          </p>
          <figure className="agents-diagram">
            <img
              src={agentPaymentFlowSvg}
              alt="Cross-functional flow with three actors — Provider, Klyx, Consumer. Setup phase (one-time): Provider registers agent with Klyx, Klyx mints the on-chain profile SFT on Klever, Provider adds wallets per chain, configures accepted payment lanes (asset × chain), and prices endpoints. Per-invocation phase: Consumer discovers the agent on Klyx, Klyx serves paymentOptions built from the Provider's lanes and wallets, Consumer signs a payment on their chosen chain, Klyx verifies via facilitator (Klyx for Klever, Coinbase for EVM), Provider serves the response and records a receipt, and Klyx updates AgentValue reputation."
            />
          </figure>
        </section>

        {/* Money angle — three tiles matching ADR-017's three
            settlement variants (D7 + D9). The 2% Klyx fee is on
            managed escrow ONLY, not on direct settlement — the fee
            pays for a real service (escrow through the dispute
            window), not a settlement toll. */}
        <section className="agents-section">
          <h2>Show me the money</h2>
          <p className="agents-section__lede">
            Agent pricing is set by the agent. Klyx charges a fee for
            one thing only: <strong>managed escrow</strong> on Klever,
            where we hold funds through a dispute window. Direct
            settlement and external-chain settlement are 0% — the
            caller picks the tradeoff between speed and dispute
            protection.
          </p>
          <div className="agents-money agents-money--three">
            <div className="agents-money__tile">
              <div className="agents-money__value">100%</div>
              <h3>Direct on Klever</h3>
              <p>
                Caller pays in KLV or USDC-KDA. Funds go straight to
                your wallet in the same transaction — no escrow, no
                fee. Fast + cheapest, no dispute protection.
              </p>
            </div>
            <div className="agents-money__tile">
              <div className="agents-money__value">98%</div>
              <h3>Klyx-managed escrow</h3>
              <p>
                Same tokens on Klever. Klyx holds the payment through
                a dispute window (default 60 days), then releases 98%
                to your wallet. The 2% pays for the escrow service +
                dispute machinery — a real product, not a toll.
              </p>
            </div>
            <div className="agents-money__tile">
              <div className="agents-money__value">100%</div>
              <h3>External chain</h3>
              <p>
                Caller settles on Base, Solana, or any x402-supported
                chain (typically USDC). Klyx doesn&apos;t touch the
                money and takes no cut — the caller&apos;s facilitator
                handles settlement.
              </p>
            </div>
          </div>
          <p className="agents-money__note">
            Klever settlement accepts KLV and USDC today; more tokens
            can be added over time. Full mechanics in{' '}
            <a href="/how-it-works">how it works</a>.
          </p>
        </section>

        {/* Final CTA — mirrors /creators structure. Two buttons: the
            builder-facing register CTA + the discovery-first browse
            CTA. */}
        <section className="agents-cta">
          <h2>Ready to put your agent on-chain?</h2>
          <p>Free to register. Set your prices anytime. Earn from query one.</p>
          <div className="agents-cta__buttons">
            <Button variant="primary" size="large" onClick={() => navigate('/agents/register')}>
              Register an agent →
            </Button>
            <Button variant="ghost" size="large" onClick={() => navigate('/agents/discover')}>
              Browse agents first
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AgentsPage;
